import { ReactNode, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import InfoTip from '../InfoTip';
import {
  CATEGORY_HINTS,
  KPI,
  KpiDefinition,
  KpiKey,
  TAIL_CATEGORIES,
  formatKpi,
} from '../../helpers/webinarKpiDefinitions';
import type { EvaluationMetrics } from '../../helpers/webinarStatisticsData';

/** Eine freie Spalte (Webinarname, Datum, Buttons …) neben den Kennzahl-Spalten. */
export interface ExtraColumn<T> {
  id: string;
  label: string;
  tooltip?: string;
  render: (row: T) => ReactNode;
  /** Ohne sortValue ist die Spalte nicht sortierbar. */
  sortValue?: (row: T) => string | number | null;
}

interface MetricsTableProps<T extends EvaluationMetrics> {
  rows: T[];
  metrics: KpiKey[];
  leading: ExtraColumn<T>[];
  trailing?: ExtraColumn<T>[];
  /** Summenzeile — kommt aus dem Backend, nie aus einem reduce ueber die Zeilen. */
  totals?: Partial<EvaluationMetrics> | null;
  totalsLabel?: string;
  rowKey: (row: T, index: number) => string;
  defaultSortBy?: string;
  defaultOrder?: 'asc' | 'desc';
  onRowClick?: (row: T) => void;
  maxHeight?: number;
}

const HEADER_SX = {
  color: 'black',
  fontWeight: 'bold',
  backgroundColor: '#e0e0e0 !important',
  whiteSpace: 'nowrap',
} as const;

/** Zusatzinfo je Zelle: Zaehler und Nenner einer Quote, fehlende CPA-Werte. */
const cellHint = (key: KpiKey, r: Partial<EvaluationMetrics>): string | null => {
  const n = (v: unknown) => Number(v ?? 0).toLocaleString('de-DE');
  switch (key) {
    case 'show_up_rate':
      return `${n(r.attendees)} Teilnehmer von ${n(r.timely)} rechtzeitig Angemeldeten`;
    case 'registration_conversion_rate':
      return `${n(r.orders)} Anfos von ${n(r.registrations)} Registrierungen`;
    case 'attendee_conversion_rate':
      return `${n(r.attendee_orders)} Anfos von ${n(r.attendees)} Teilnehmern`;
    case 'cpa_revenue':
    case 'avg_cpa_per_order':
    case 'cpa_per_registration':
    case 'cpa_per_attendee':
      return r.orders_without_cpa
        ? `${n(r.orders_without_cpa)} Anfo(s) ohne CPA-Wert sind nicht enthalten`
        : null;
    default:
      return null;
  }
};

const tailRank = (category: string | undefined) =>
  category === undefined ? -1 : TAIL_CATEGORIES.indexOf(category);

/**
 * Sortierbare Kennzahl-Tabelle. Kopf-Tooltips, Formate und Farben kommen aus
 * webinarKpiDefinitions.ts. Restposten (Sonstige, Ohne Anmeldung, …) bleiben beim Sortieren unten.
 */
function MetricsTable<T extends EvaluationMetrics>({
  rows,
  metrics,
  leading,
  trailing = [],
  totals,
  totalsLabel = 'Insgesamt',
  rowKey,
  defaultSortBy,
  defaultOrder = 'desc',
  onRowClick,
  maxHeight,
}: MetricsTableProps<T>) {
  const [sortBy, setSortBy] = useState<string>(defaultSortBy ?? metrics[0] ?? leading[0]?.id);
  const [order, setOrder] = useState<'asc' | 'desc'>(defaultOrder);

  const extraById = useMemo(() => {
    const m = new Map<string, ExtraColumn<T>>();
    [...leading, ...trailing].forEach((c) => m.set(c.id, c));
    return m;
  }, [leading, trailing]);

  const sorted = useMemo(() => {
    const valueOf = (row: T): string | number | null => {
      const extra = extraById.get(sortBy);
      if (extra?.sortValue) return extra.sortValue(row);
      const v = (row as unknown as Record<string, unknown>)[sortBy];
      return v === null || v === undefined ? null : (v as number);
    };
    return [...rows].sort((a, b) => {
      const ta = tailRank(a.category);
      const tb = tailRank(b.category);
      if (ta !== tb) return ta - tb;
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp =
        typeof va === 'string' || typeof vb === 'string'
          ? String(va).localeCompare(String(vb), 'de')
          : va - vb;
      return order === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortBy, order, extraById]);

  const toggle = (id: string) => {
    if (sortBy === id) setOrder(order === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(id);
      setOrder('desc');
    }
  };

  const metricCell = (key: KpiKey, r: Partial<EvaluationMetrics>, bold = false) => {
    const def: KpiDefinition = KPI[key];
    const value = (r as Record<string, number | null | undefined>)[key];
    const hint = cellHint(key, r);
    const text = formatKpi(key, value);
    const needsMark = hint && key.startsWith('cpa') ? ' *' : '';
    return (
      <TableCell
        key={key}
        align="right"
        sx={{
          color: def.color ?? 'black',
          fontWeight: bold ? 'bold' : undefined,
          whiteSpace: 'nowrap',
        }}
      >
        {hint ? (
          <Tooltip title={hint} arrow>
            <span style={{ cursor: 'help' }}>
              {text}
              {needsMark}
            </span>
          </Tooltip>
        ) : (
          text
        )}
      </TableCell>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ backgroundColor: '#ffffff', ...(maxHeight ? { maxHeight } : {}) }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {leading.map((c) => (
              <TableCell key={c.id} sx={HEADER_SX}>
                {c.sortValue ? (
                  <TableSortLabel
                    active={sortBy === c.id}
                    direction={sortBy === c.id ? order : 'asc'}
                    onClick={() => toggle(c.id)}
                  >
                    {c.label}
                  </TableSortLabel>
                ) : (
                  c.label
                )}
                {c.tooltip && <InfoTip text={c.tooltip} />}
              </TableCell>
            ))}
            {metrics.map((key) => {
              const def: KpiDefinition = KPI[key];
              return (
                <TableCell
                  key={key}
                  align="right"
                  sx={{ ...HEADER_SX, color: def.color ?? 'black' }}
                >
                  <TableSortLabel
                    active={sortBy === key}
                    direction={sortBy === key ? order : 'asc'}
                    onClick={() => toggle(key)}
                  >
                    {def.short}
                  </TableSortLabel>
                  <InfoTip text={`${def.label}: ${def.tooltip}\n\nFormel: ${def.formel}`} />
                </TableCell>
              );
            })}
            {trailing.map((c) => (
              <TableCell key={c.id} sx={HEADER_SX}>
                {c.label}
                {c.tooltip && <InfoTip text={c.tooltip} />}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow
              key={rowKey(row, i)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{
                cursor: onRowClick ? 'pointer' : undefined,
                backgroundColor: tailRank(row.category) >= 0 ? '#fafafa' : undefined,
              }}
            >
              {leading.map((c) => (
                <TableCell key={c.id} sx={{ color: 'black' }}>
                  {c.render(row)}
                </TableCell>
              ))}
              {metrics.map((key) => metricCell(key, row))}
              {trailing.map((c) => (
                <TableCell key={c.id} sx={{ color: 'black' }}>
                  {c.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {totals && (
            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
              <TableCell colSpan={leading.length} sx={{ color: 'black', fontWeight: 'bold' }}>
                {totalsLabel}
              </TableCell>
              {metrics.map((key) => metricCell(key, totals, true))}
              {trailing.length > 0 && <TableCell colSpan={trailing.length} />}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Erste Spalte einer Gruppierungstabelle: Kategoriename, bei Restposten mit Erklaerung. */
export const categoryColumn = <T extends EvaluationMetrics>(
  label: string,
  tooltip?: string,
): ExtraColumn<T> => ({
  id: 'category',
  label,
  tooltip,
  sortValue: (r) => r.category,
  render: (r) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {r.category}
      {CATEGORY_HINTS[r.category] && <InfoTip text={CATEGORY_HINTS[r.category]} />}
    </Box>
  ),
});

/** Standard-Kennzahlen einer Kanal-Gruppierung. */
export const CHANNEL_METRICS: KpiKey[] = [
  'registrations',
  'timely',
  'attendees',
  'show_up_rate',
  'orders',
  'registration_conversion_rate',
  'attendee_conversion_rate',
  'cpa_revenue',
  'cpa_per_registration',
  'cpa_per_attendee',
  'ltv_revenue',
  'paid_revenue',
];

export default MetricsTable;
